import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const STATI = [
  { v: "nuovo", l: "Nuovo", cls: "border-[#7D2E3F] text-[#7D2E3F]" },
  { v: "contattato", l: "Contattato", cls: "border-[#B08D2E] text-[#B08D2E]" },
  { v: "in_trattativa", l: "In trattativa", cls: "border-[#3A3D42] text-[#3A3D42]" },
  { v: "chiuso", l: "Chiuso", cls: "border-[#5C5657] text-[#5C5657]" },
];

const formatDateTimeIT = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function AgenziaLead() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/agency/leads").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const currentStato = (l) => l.status || "nuovo";
  const filtered = filter === "all" ? items : items.filter((l) => currentStato(l) === filter);

  const setStato = async (lid, v) => {
    try {
      await api.patch(`/agency/leads/${lid}`, { status: v });
      load();
      toast.success("Stato aggiornato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveNote = async (lid) => {
    try {
      await api.patch(`/agency/leads/${lid}`, { note_agenzia: noteDraft });
      setExpanded(null); setNoteDraft(""); load();
      toast.success("Nota salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const counts = STATI.reduce((acc, s) => ({...acc, [s.v]: items.filter((l) => currentStato(l) === s.v).length}), { all: items.length });

  return (
    <div data-testid="agenzia-lead">
      <div className="label-uppercase">La mia agenzia</div>
      <h1 className="font-serif text-4xl mt-2 mb-6">Le mie richieste</h1>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="label-uppercase text-[#5C5657] mr-1">Filtra:</span>
        <button data-testid="lead-filter-all" onClick={() => setFilter("all")}
                className={`text-xs label-uppercase px-3 py-1 border ${filter === "all" ? "border-[#7D2E3F] text-[#7D2E3F] bg-[#FDFDFC]" : "border-[#E6E4E0] text-[#5C5657]"}`}>
          Tutte ({counts.all})
        </button>
        {STATI.map((s) => (
          <button key={s.v} data-testid={`lead-filter-${s.v}`} onClick={() => setFilter(s.v)}
                  className={`text-xs label-uppercase px-3 py-1 border ${filter === s.v ? "border-[#7D2E3F] text-[#7D2E3F] bg-[#FDFDFC]" : "border-[#E6E4E0] text-[#5C5657]"}`}>
            {s.l} ({counts[s.v]})
          </button>
        ))}
      </div>

      <table className="admin-table w-full bg-white border border-[#E6E4E0]">
        <thead><tr><th>Cliente</th><th>Contatti</th><th>Territorio</th><th>Tipo</th><th>Ricevuta</th><th>Stato</th><th></th></tr></thead>
        <tbody>
          {filtered.map((l) => (
            <React.Fragment key={l.id}>
              <tr data-testid={`ag-lead-${l.id}`}>
                <td className="font-medium">{l.nome}</td>
                <td className="text-xs"><div>{l.email}</div><div>{l.telefono}</div></td>
                <td className="text-xs">{l.comune ? `${l.comune} · ` : ""}{l.provincia}</td>
                <td className="text-xs">{l.tipo_immobile}</td>
                <td className="text-xs whitespace-nowrap">{formatDateTimeIT(l.created_at)}</td>
                <td>
                  <select data-testid={`ag-lead-status-${l.id}`} value={currentStato(l)}
                          onChange={(e) => setStato(l.id, e.target.value)}
                          className="text-xs border border-[#E6E4E0] px-2 py-1 bg-white">
                    {STATI.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                </td>
                <td className="text-right">
                  <button data-testid={`ag-lead-toggle-${l.id}`}
                          onClick={() => { if (expanded === l.id) { setExpanded(null); } else { setExpanded(l.id); setNoteDraft(l.note_agenzia || ""); } }}
                          className="text-xs label-uppercase text-[#7D2E3F]">
                    {expanded === l.id ? "Chiudi" : "Note"}
                  </button>
                </td>
              </tr>
              {expanded === l.id && (
                <tr><td colSpan="7" className="bg-[#FDFDFC] border-t-0">
                  <div className="p-4">
                    {l.note && (
                      <div className="mb-3">
                        <div className="label-uppercase text-[#5C5657] mb-1">Messaggio del cliente</div>
                        <div className="text-sm text-[#3A3D42]">{l.note}</div>
                      </div>
                    )}
                    <div>
                      <div className="label-uppercase text-[#5C5657] mb-1">Le mie note (visibili solo alla tua agenzia)</div>
                      <textarea rows="3" value={noteDraft} data-testid={`ag-lead-note-${l.id}`}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                className="admin-input" placeholder="Ultimo contatto, esito visita, aspettative cliente…" />
                      <div className="text-right mt-2">
                        <button onClick={() => saveNote(l.id)} data-testid={`ag-lead-save-note-${l.id}`} className="btn-primary text-xs">Salva nota</button>
                      </div>
                    </div>
                  </div>
                </td></tr>
              )}
            </React.Fragment>
          ))}
          {filtered.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-[#5C5657]">Nessuna richiesta per questo filtro</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
